import {LeadAppointmentSmsSentEventDataApiDto, LeadEventApiDto, LeadSmsSentEventDataApiDto} from "../../../api/model/rest";
import LeadEvent, {LeadEventTypeEnum, SmsEventSubtypeEnum} from "./LeadEvent";
import AuthenticatedEntity from "../../user/AuthenticatedEntity";
import {option, Option} from "ts-option";
import {Icon} from "../../../hiberUiKit/src/atoms/Icon/Icon";
import {IconColor} from "../../../hiberUiKit/src/molecules/StreamItem/StreamItemIcon";
import {getIndividualContact} from "../../../store/view/contact";
import {store} from "../../../navigation/ReduxProvider";
import moment = require("moment");

export default class LeadSmsSentEvent extends LeadEvent {
	/**
	 * @param {string} id identifier of event.
	 * @param {string} leadId lead id.
	 * @param {LeadEventTypeEnum} type type of the event.
	 * @param {AuthenticatedEntity} entity entity which creates the event.
	 * @param {moment.Moment} creationDate creation date of event.
	 * @param {string} referenceNumber lead reference number.
	 * @param data sms send event data
	 */
	constructor(public readonly id: string,
		public readonly leadId: string,
		public readonly type: LeadEventTypeEnum,
		public readonly entity: AuthenticatedEntity,
		public readonly creationDate: moment.Moment,
		public readonly referenceNumber: string,
		public readonly data: LeadSmsSentEventData | LeadAppointmentSmsSentEventData) {
		super(id, leadId, type, entity, creationDate, referenceNumber);
	}

	public static fromJson(json: LeadEventApiDto): LeadSmsSentEvent {
		let data: LeadSmsSentEventData | LeadAppointmentSmsSentEventData;
		const jsonData = json.data as LeadSmsSentEventDataApiDto;
		switch (jsonData.subtype) {
			case SmsEventSubtypeEnum.AppointmentConfirmationSms:
			case SmsEventSubtypeEnum.SeOnTheWaySms:
			case SmsEventSubtypeEnum.SeReminderSms:
			case SmsEventSubtypeEnum.CustomerReminderSms:
				data = LeadAppointmentSmsSentEventData.fromJson(json.data as LeadAppointmentSmsSentEventDataApiDto);
				break;
			default:
				data = LeadSmsSentEventData.fromJson(json.data as LeadSmsSentEventDataApiDto);
				break;
		}
		const entity = AuthenticatedEntity.fromJson(json.entity);
		return new LeadSmsSentEvent(json.id,
			json.leadId,
			LeadEventTypeEnum.SmsSent,
			entity,
			moment(json.createdAt),
			json.referenceNumber,
			data);
	}

	getIcon() {
		return Icon.SMS;
	}

	getIconColor(): IconColor {
		return IconColor.Burgundy;
	}

	getActivityAdditionalMessages(): string[] {
		return [`Message: ${this.data.message}`];
	}

	getActivityType(): string {
		return `SMS`
	}

	getActivityMessage(): string {
		let eventMessage: string;
		switch (this.data.subtype) {
			case SmsEventSubtypeEnum.FreeTextContactSms:
				eventMessage = "Free text SMS to contact";
				break;
			case SmsEventSubtypeEnum.AppointmentConfirmationSms:
				eventMessage = "Appointment confirmation SMS";
				break;
			case SmsEventSubtypeEnum.SeOnTheWaySms:
				eventMessage = "SMS to contact that sales engineer is on the way";
				break;
			case SmsEventSubtypeEnum.SeReminderSms:
				eventMessage = "Reminder SMS to sales engineer";
				break;
			case SmsEventSubtypeEnum.CustomerReminderSms:
				eventMessage = "Appointment reminder SMS to contact";
				break;
			default:
				eventMessage = "SMS to unknown receiver";
		}

		const receiver = this.data.receiverId.flatMap(r => getIndividualContact(r, store.getState()));
		const receiverName = receiver.isDefined ? ` - ${receiver.get.data.displayName()}` : "";
		const receiverPhones =
			` (${this.data.receiverPhones.length ? this.data.receiverPhones.join(", ")
				: "unknown"})`;
		return eventMessage + receiverName + receiverPhones;
	}

}

export class LeadSmsSentEventData {
	constructor(public readonly receiverId: Option<string>,
		public readonly receiverPhones: string[],
		public readonly message: string,
		public readonly subtype: string) {}

	public static fromJson(json: LeadSmsSentEventDataApiDto): LeadSmsSentEventData {
		return new LeadSmsSentEventData(option(json.receiverId), json.receiverPhones, json.message, json.subtype);
	}
}

export class LeadAppointmentSmsSentEventData {
	constructor(public readonly receiverId: Option<string>,
		public readonly receiverPhones: string[],
		public readonly appointmentId: Option<string>,
		public readonly message: string,
		public readonly subtype: string) {}

	public static fromJson(json: LeadAppointmentSmsSentEventDataApiDto): LeadAppointmentSmsSentEventData {
		return new LeadAppointmentSmsSentEventData(option(json.receiverId), json.receiverPhones,
			option(json.appointmentId), json.message, json.subtype);
	}
}
